import dayjs from 'dayjs';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isToday from 'dayjs/plugin/isToday';
import relativeTime from 'dayjs/plugin/relativeTime';
import type {DeliveryDate} from '../interfaces/delivery.date.type';
import type {BaseOrderLine, OrderLine} from '../interfaces/order.line.type';
import {OrderStatus} from '../interfaces/order.status.enum';
import type {Order} from '../interfaces/order.type';

dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isLeapYear);
dayjs.extend(isToday);
dayjs.extend(relativeTime);

/**
 * Formats a given epoch date number to a readable format;
 *
 * @param date
 */
export const formatDate = (date: number): string => {
	return dayjs(new Date(date)).format('DD/MM/YYYY');
};

/**
 * Get the text 'Today' if date is today instead of the hours.
 *
 * @param date
 * @param todayText
 * @param baseDate
 */
export const getModifiedDate = (date: number, todayText: string, baseDate: Date = new Date()): string => {
	return dayjs(new Date(date)).isToday() ? todayText : dayjs(date).from(dayjs(baseDate), true) + ' ago';
};

export const formatOrderDate = (orderDate: number) => {
	const date = new Date(orderDate);
	const options: Intl.DateTimeFormatOptions = {year: 'numeric', month: '2-digit', day: '2-digit'};
	return date.toLocaleDateString('nl-NL', options);
};

export const formatLastModifiedDate = (date: number) => {
	return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
};

/**
 * Normalizes the delivery date data to a human-readable string.
 *
 * @param deliveryDate
 * @param shortLabel
 * @param shortWeekText
 * @param longWeekText
 */
export const getNormalizedWeekIncludingLabel = (deliveryDate: DeliveryDate, shortLabel: boolean, shortWeekText: string, longWeekText: string): string => {
	if (!deliveryDate) {
		return '';
	}

	const label: string = shortLabel ? shortWeekText : longWeekText;
	return `${label} ${deliveryDate.week} - ${deliveryDate.year}`;
};

/**
 * Gets a comma separated string of all the weeks for the given order lines.
 *
 * @param orderLines
 * @param shortWeekTest
 * @param longWeekText
 */
export const getWeeksForOrderLines = (orderLines: OrderLine[], shortWeekTest: string, longWeekText: string): string => {
	return getUniqueDeliveryDatesForOrderLines(orderLines)
		.map((deliveryDate: DeliveryDate) => getNormalizedWeekIncludingLabel(deliveryDate, true, shortWeekTest, longWeekText))
		.join(', ');
};

/**
 * Returns the year and week numbers for a given year and week string
 *
 * @param deliveryDateString
 */
export const getYearAndWeekFromString = (deliveryDateString: string): DeliveryDate => {
	if ((!deliveryDateString || /^\s*$/.test(deliveryDateString))) {
		throw new Error('Delivery date string is empty or null');
	}

	const yearAndWeek: string[] = deliveryDateString.split('-');
	return {
		year: parseInt(yearAndWeek[0]), week: parseInt(yearAndWeek[1])
	};
};

/**
 * Returns a sorted array of unique delivery dates for the given order lines.
 *
 * @param orderLines
 */
export const getUniqueDeliveryDatesForOrderLines = (orderLines: BaseOrderLine[]): DeliveryDate[] => {
	if (!orderLines) {
		return [];
	}

	// A little workaround for getting the unique delivery dates
	// For explanation, see: https://stackoverflow.com/a/39997167/639033
	const uniqueDeliveryDates: string[] = [...new Set(orderLines.map((line) => `${line.year}-${line.week}`))];

	return uniqueDeliveryDates.map((deliveryDateString: string) => {
		return getYearAndWeekFromString(deliveryDateString);
	}).sort((deliveryDate1: DeliveryDate, deliveryDate2: DeliveryDate): number => {
		if (deliveryDate1.year > deliveryDate2.year) {
			return 1;
		}

		if (deliveryDate1.year < deliveryDate2.year) {
			return -1;
		}

		if (deliveryDate1.week > deliveryDate2.week) {
			return 1;
		}

		if (deliveryDate1.week < deliveryDate2.week) {
			return -1;
		}

		return 0;
	});
};

/**
 * Returns the total amount of order lines for the given order.
 *
 * @param orderLine
 * @param orderLines
 * @param maxOrderAmount
 */
export const isInvalidTotalAmountForVariety = (orderLine: BaseOrderLine, orderLines: BaseOrderLine[], maxOrderAmount: number | null) => {
	if (maxOrderAmount === 0 || !maxOrderAmount) {
		return true;
	}

	const orderLinesOfSameVariety = orderLines
		.filter((line: BaseOrderLine) => {
			return line && line.articleId === orderLine.articleId
				&& line.articleName === orderLine.articleName
				&& line.week === orderLine.week
				&& line.year === orderLine.year
				&& line.formId === orderLine.formId;
		});

	if (orderLinesOfSameVariety.length === 0) {
		return true;
	}

	const sumOfAllVarieties = orderLinesOfSameVariety
		.map((line: BaseOrderLine) => line.amount)
		.reduce((prev: number, next: number) => prev + next);

	return sumOfAllVarieties > maxOrderAmount;
};

/**
 * Returns true if the desired amount is invalid.
 *
 * @param amount
 * @param step
 * @param maxOrderAmount
 */
export const isInvalidAmount = (amount: number, step: number, maxOrderAmount: number | null) => {
	if (amount === undefined) {
		return true;
	}

	if (amount === null) {
		return false;
	}

	if (maxOrderAmount === 0) {
		return true;
	}

	if (!maxOrderAmount) {
		return true;
	}

	if (amount < 1) {
		return true;
	}

	if (!Number.isInteger(amount / step)) {
		return true;
	}

	return amount > maxOrderAmount;
};
